import { useCallback, useEffect, useMemo } from 'react';

import { CUSTOMER_USERS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCustomerUserDetailsAppwideDrawer } from '@src/components/DetailsDrawer/Customer/CustomerUserDetailsDrawer';
import { useCounterpartiesFilter, useCustomerRolesFilter, useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useCustomerUsers, useCustomers, useCustomersContext } from '@src/hooks/useCustomer';
import { useCustomerMarketAccounts } from '@src/providers';
import { useBlotterState } from '@src/providers/AppConfigProvider';
import { usePricingRules } from '@src/providers/PricingRulesProvider';
import {
  ACTION,
  Box,
  Button,
  ButtonVariants,
  CustomerRolePillWrapper,
  CustomerUser,
  Divider,
  FormControlSizes,
  FormTable,
  HStack,
  Icon,
  IconButton,
  IconName,
  Input,
  LocalFilterInput,
  ModeEnum,
  MultiSelectCheckboxDropdown,
  NotificationVariants,
  Panel,
  PanelActions,
  PanelContent,
  PanelHeader,
  ToggleGroup,
  useCallbackRef,
  useFormTable,
  useGlobalToasts,
  useObservableValue,
  type Column,
  type MarketAccount,
  type ToggleGroupOption,
} from '@talos/kyoko';
import type { ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { entries } from 'lodash-es';
import { useCustomerUsersContextMenu } from './useCustomerUsersContextMenu';
import {
  useCustomerUsersTableFilters,
  type CustomerUsersModeFilter,
  type CustomerUsersTableFilter,
} from './useCustomerUsersTableFilters';

const READ_ORDERS = 'read::orders';
const WRITE_ORDERS = 'write::orders';

const MODE_FILTER_OPTIONS = [
  { value: 'All', displayName: 'All' },
  { value: 'Enabled', displayName: 'Enabled' },
  { value: 'Disabled', displayName: 'Disabled' },
] satisfies ToggleGroupOption<CustomerUsersModeFilter>[];

interface CustomerUsersProps {
  layout?: 'page' | 'panel';
  quickFilterText?: string;
  onQuickFilterTextChanged?: (text: string) => void;
}

export const CustomerUsers = ({
  layout = 'page',
  quickFilterText,
  onQuickFilterTextChanged,
}: CustomerUsersProps = {}) => {
  const isPanelLayout = layout === 'panel';
  const availableCustomerFilterProperty = useCounterpartiesFilter({ label: 'Customer' });
  const availableRoleFilterProperty = useCustomerRolesFilter();
  const availableFilterableProperties = useMemo(
    () => [availableCustomerFilterProperty, availableRoleFilterProperty] as const,
    [availableCustomerFilterProperty, availableRoleFilterProperty]
  );
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_USERS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );
  const [customerFilterProperty, roleFilterProperty] = filterableProperties;
  const { filterValueCustomerUsers, setFilterValueCustomerUsers } = useBlotterState();
  const { modeFilter, setModeFilter, filter, changeFilter, clientLocalFilter, isCustomerFilterExternallyControlled } =
    useCustomerUsersTableFilters({ externallyControlledFilter });

  const { isAuthorized } = useRoleAuth();
  const isAdmin = isAuthorized(ACTION.EDIT_CUSTOMER_USERS);

  const customers = useCustomers();
  const customerUsers = useCustomerUsers();
  const { pricingRules } = usePricingRules();
  const { add: addToast } = useGlobalToasts();
  const { getContextMenuItems, dialogs } = useCustomerUsersContextMenu();
  const openCustomerUserDetailsDrawer = useCustomerUserDetailsAppwideDrawer();

  const customersService = useCustomersContext();
  const customerRoles = useObservableValue(() => customersService.customerRoles, [customersService.customerRoles]);
  const customerRolesByName = useMemo(() => new Map(customerRoles?.map(role => [role.Name, role])), [customerRoles]);
  // root Customer records have a TradeSubAccount and HedgeSubAccount field, whereas the CustomerUser records don't
  const customerIsDisabledByCounterparty = useMemo<Map<string, boolean>>(() => {
    return (
      pricingRules
        // [DEAL-5055] Only consider parent rows, do not trigger from disabled symbol rows.
        ?.filter(rule => rule.Symbol == null && !!rule.TradeSubAccount)
        ?.reduce((acc, rule) => {
          acc.set(rule.Counterparty, rule.Mode === ModeEnum.Disabled);
          return acc;
        }, new Map()) ?? new Map()
    );
  }, [pricingRules]);

  // Column defs are declared before useFormTable; useCallbackRef keeps the handler referentially stable.
  const updateCustomerMode = useCallbackRef(
    (email: string, customerUserId: string, displayName: string, enabled: boolean) => {
      return customersService
        .updateCustomerUser({
          CustomerUserID: customerUserId,
          Mode: enabled ? ModeEnum.Enabled : ModeEnum.Disabled,
        })
        .then(response => {
          addToast({
            text: `${displayName} ${enabled ? 'enabled' : 'disabled'}`,
            variant: NotificationVariants.Positive,
          });

          const mode =
            response.data.find(user => user.CustomerUserID === customerUserId)?.Mode ?? response.data.at(0)?.Mode;
          if (mode == null) {
            return;
          }

          const row = formTable.getRow(email);
          row?.setData(new CustomerUser({ ...row.data, Mode: mode }));
          // setData refreshes row data but doesn't re-run the external filter, so a row whose
          // new Mode no longer matches the active filter would stay visible until the next refresh.
          formTable.onFilterChanged();
        })
        .catch((e: ErrorEvent) => {
          addToast({
            text: `Could not ${enabled ? 'enable' : 'disable'} ${displayName}: ${e?.toString()}`,
            variant: NotificationVariants.Negative,
          });
          if (customerUsers != null) {
            formTable.reset(customerUsers, [], []);
          }
        });
    }
  );

  const addEditCustomerUser = useCallback(
    (customerUser?: CustomerUser) => {
      openCustomerUserDetailsDrawer(customerUser?.CustomerUserID);
    },
    [openCustomerUserDetailsDrawer]
  );

  const { customerMarketAccountsByName } = useCustomerMarketAccounts();
  const { enableCustomerAccountRestrictions } = useFeatureFlag();
  const handleCustomerFilterChange = useCallback(
    (counterparties: string[]) => {
      changeFilter(curr => ({
        ...curr,
        Counterparties: counterparties,
      }));
    },
    [changeFilter]
  );

  const handleRoleFilterChange = useCallback(
    (roles: string[]) => {
      changeFilter(curr => ({
        ...curr,
        Roles: roles,
      }));
    },
    [changeFilter]
  );

  const columns = useMemo<Column[]>(
    () => [
      {
        type: 'modeWithMessage',
        initialWidth: 100,
        field: 'Mode',
        title: 'Enabled',

        initialHide: !isAdmin,
        hideInColumnsToolPanel: !isAdmin,
        params: {
          cellRendererParams: ({ data }: ICellRendererParams<CustomerUser>) => ({
            checked: data?.Mode === ModeEnum.Enabled,
            tooltip:
              data?.Mode === ModeEnum.Enabled && customerIsDisabledByCounterparty.get(data?.Counterparty) ? (
                <Box textAlign="center">
                  Customer is currently disabled
                  <br />
                  under Customers and Pricing
                </Box>
              ) : null,
            onChange: (enabled: boolean) => {
              if (data == null) {
                return;
              }
              updateCustomerMode(data.Email, data.CustomerUserID, data.DisplayName, enabled);
            },
          }),
        },
      },
      {
        type: 'counterparty',
        field: 'Counterparty',
        title: 'Customer',
      },
      { type: 'text', field: 'DisplayName', title: 'Name', initialWidth: 125 },
      {
        type: 'email',
        field: 'Email',
        initialWidth: 250,
      },
      {
        type: 'custom',
        title: 'Roles',
        colId: 'Roles',
        initialWidth: 200,
        params: {
          valueGetter: ({ data }: { data: CustomerUser }) => {
            return (data.Roles ?? []).map(customerRole => {
              return typeof customerRole === 'string'
                ? customerRolesByName?.get(customerRole)?.DisplayName
                : customerRole.DisplayName;
            });
          },
          cellRenderer: ({ value, context }: { value: string[]; context: any }) => {
            return (
              <HStack gap="spacing4" justifyContent="flex-start">
                {value.map(role => (
                  <CustomerRolePillWrapper key={role} theme={context.current.theme}>
                    {role}
                  </CustomerRolePillWrapper>
                ))}
              </HStack>
            );
          },
          tooltipValueGetter: ({ value }: { value: string[] }) => {
            return value.join(', ');
          },
        },
      },
      { type: 'text', field: 'ExternalID', initialWidth: 250 },
      {
        type: 'custom',
        title: 'View Accounts',
        colId: 'ViewAccounts',
        initialHide: !enableCustomerAccountRestrictions,
        initialWidth: 250,
        params: {
          valueGetter: ({ data }: { data: CustomerUser }) => {
            return getPermissionedAccounts(data, READ_ORDERS, customerMarketAccountsByName);
          },
        },
      },
      {
        type: 'custom',
        title: 'Trade Accounts',
        colId: 'TradeAccounts',
        initialHide: !enableCustomerAccountRestrictions,
        initialWidth: 250,
        params: {
          valueGetter: ({ data }: { data: CustomerUser }) => {
            return getPermissionedAccounts(data, WRITE_ORDERS, customerMarketAccountsByName);
          },
        },
      },
      { type: 'filler', id: 'filler1' },
      {
        type: 'iconButton',
        id: 'edit-button',
        pinned: 'right',
        initialWidth: 40,
        initialHide: !isAdmin,
        suppressColumnsToolPanel: !isAdmin,
        params: {
          onClick: ({ node }: { node: IRowNode }) => {
            addEditCustomerUser(new CustomerUser(node.data));
          },
          icon: IconName.Pencil,
        },
      },
    ],
    [
      customerRolesByName,
      enableCustomerAccountRestrictions,
      customerMarketAccountsByName,
      customerIsDisabledByCounterparty,
      addEditCustomerUser,
      isAdmin,
      updateCustomerMode,
    ]
  );

  // look into whether we should change this to useBlotterTable and <BlotterTable /> like in Settings/Users instead
  const formTable = useFormTable<CustomerUser>({
    rowID: 'Email',
    data: customerUsers ?? [],
    columns: columns ?? [],
    clientLocalFilter,
    quickSearchParams: {
      filterText: isPanelLayout ? quickFilterText : filterValueCustomerUsers,
      onFilterTextChange: isPanelLayout ? onQuickFilterTextChanged : setFilterValueCustomerUsers,
    },
  });

  useEffect(() => {
    if (customerUsers != null) {
      formTable.reset(customerUsers, [], []);
    }
    // formTable is intentionally omitted — not stable and causes an infinite render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerUsers]);

  const formTableGridOptions = useMemo(() => {
    if (formTable.gridOptions == null) {
      return null;
    }

    return {
      ...formTable.gridOptions,
      getContextMenuItems,
    };
  }, [formTable.gridOptions, getContextMenuItems]);

  const headerActions = (
    <PanelActions>
      <LocalFilterInput
        placeholder="Filter by Customer, Name, or Email"
        value={filterValueCustomerUsers}
        onChange={setFilterValueCustomerUsers}
      />
      {isAdmin && (
        <Button
          startIcon={IconName.Plus}
          onClick={() => addEditCustomerUser()}
          variant={ButtonVariants.Positive}
          disabled={!isAuthorized(ACTION.EDIT_CUSTOMER_USERS)}
          data-testid="add-user-button"
        >
          Add User
        </Button>
      )}
    </PanelActions>
  );

  const headerFilters = (
    <PanelActions flex="1 0 100%" mt="spacing8">
      <ToggleGroup
        options={MODE_FILTER_OPTIONS}
        value={modeFilter}
        onChange={setModeFilter}
        size={FormControlSizes.Default}
        data-testid="customer-users-mode-filter"
        defaultSelectedVariant={ButtonVariants.Priority}
      />
      <Box w="280px">
        <MultiSelectCheckboxDropdown
          prefix="Customer"
          defaultLabel="All Customers"
          property={customerFilterProperty}
          selections={filter.Counterparties ?? []}
          onSelectionsChange={handleCustomerFilterChange}
          buttonTestID="customer-users-customer-filter"
        />
      </Box>
      <Box w="220px">
        <MultiSelectCheckboxDropdown
          prefix="Roles"
          defaultLabel="All Roles"
          property={roleFilterProperty}
          selections={filter.Roles ?? []}
          onSelectionsChange={handleRoleFilterChange}
          buttonTestID="customer-users-roles-filter"
        />
      </Box>
    </PanelActions>
  );

  if (customerUsers == null || customers == null) {
    return null;
  }
  if (isPanelLayout) {
    return (
      <>
        <HStack h="100%" w="100%" gap="spacing2" overflow="hidden">
          <Panel h="100%" w="100%">
            <CustomerUsersPanelHeader
              addEditCustomerUser={addEditCustomerUser}
              customerFilterProperty={customerFilterProperty}
              filter={filter}
              isAdmin={isAdmin}
              isCustomerFilterExternallyControlled={isCustomerFilterExternallyControlled}
              isAddUserDisabled={!isAuthorized(ACTION.EDIT_CUSTOMER_USERS)}
              modeFilter={modeFilter}
              onQuickFilterTextChanged={onQuickFilterTextChanged}
              quickFilterText={quickFilterText}
              roleFilterProperty={roleFilterProperty}
              setModeFilter={setModeFilter}
              onCustomerFilterChange={handleCustomerFilterChange}
              onRoleFilterChange={handleRoleFilterChange}
            />
            <PanelContent px={0} pb={0} flex={1} minHeight={0} overflow="hidden">
              <FormTable
                {...formTable}
                gridOptions={formTableGridOptions}
                onRowDoubleClicked={params => addEditCustomerUser(new CustomerUser(params.data))}
              />
            </PanelContent>
          </Panel>
        </HStack>
        {dialogs}
      </>
    );
  }

  return (
    <>
      <HStack h="100%" w="100%" gap="spacing2" overflow="hidden">
        <Panel h="100%" w="100%">
          <PanelHeader flexWrap="wrap">
            <h2>Customer Users</h2>
            {headerActions}
            {headerFilters}
          </PanelHeader>
          <PanelContent>
            <FormTable
              {...formTable}
              gridOptions={formTableGridOptions}
              onRowDoubleClicked={params => addEditCustomerUser(new CustomerUser(params.data))}
            />
          </PanelContent>
        </Panel>
      </HStack>
      {dialogs}
    </>
  );
};

function getPermissionedAccounts(user: CustomerUser, permission: string, accountMap: Map<string, MarketAccount>) {
  const accountPerms = entries(user.MarketAccountAuthorizations).filter(([_, perms]) => perms.includes(permission));

  if (accountPerms.length === 0) {
    return 'All';
  }

  return accountPerms.map(([account, _]) => accountMap.get(account)?.SourceAccountID || account).join(', ');
}

interface CustomerUsersPanelHeaderProps {
  addEditCustomerUser: (customerUser?: CustomerUser) => void;
  customerFilterProperty: ReturnType<typeof useCounterpartiesFilter>;
  filter: CustomerUsersTableFilter;
  isAdmin: boolean;
  isCustomerFilterExternallyControlled: boolean;
  isAddUserDisabled: boolean;
  modeFilter: CustomerUsersModeFilter;
  onQuickFilterTextChanged?: (text: string) => void;
  quickFilterText?: string;
  roleFilterProperty: ReturnType<typeof useCustomerRolesFilter>;
  setModeFilter: (modeFilter: CustomerUsersModeFilter) => void;
  onCustomerFilterChange: (counterparties: string[]) => void;
  onRoleFilterChange: (roles: string[]) => void;
}

function CustomerUsersPanelHeader({
  addEditCustomerUser,
  customerFilterProperty,
  filter,
  isAdmin,
  isCustomerFilterExternallyControlled,
  isAddUserDisabled,
  modeFilter,
  onQuickFilterTextChanged,
  quickFilterText,
  roleFilterProperty,
  setModeFilter,
  onCustomerFilterChange,
  onRoleFilterChange,
}: CustomerUsersPanelHeaderProps) {
  return (
    <PanelHeader p={0} px="spacing12" minHeight="40px" alignItems="center" w="100%">
      <HStack flexWrap="wrap" justifyContent="flex-start" gap="spacing4" ml="auto">
        <Box py="spacing8">
          <Input
            data-testid="filter-search"
            placeholder="Filter by Customer, Name, or Email"
            value={quickFilterText ?? ''}
            onChange={event => onQuickFilterTextChanged?.(event.target.value)}
            clearable={true}
            prefix={<Icon icon={IconName.Search} />}
            width="275px"
            size={FormControlSizes.Small}
          />
        </Box>
        <Box py="spacing8">
          <ToggleGroup
            options={MODE_FILTER_OPTIONS}
            value={modeFilter}
            onChange={setModeFilter}
            size={FormControlSizes.Small}
            data-testid="customer-users-mode-filter"
            defaultSelectedVariant={ButtonVariants.Priority}
          />
        </Box>
        {!isCustomerFilterExternallyControlled && (
          <Box w="280px" py="spacing8">
            <MultiSelectCheckboxDropdown
              prefix="Customer"
              defaultLabel="All Customers"
              property={customerFilterProperty}
              selections={filter.Counterparties ?? []}
              onSelectionsChange={onCustomerFilterChange}
              buttonTestID="customer-users-customer-filter"
              size={FormControlSizes.Small}
            />
          </Box>
        )}
        <Box w="220px" py="spacing8">
          <MultiSelectCheckboxDropdown
            prefix="Roles"
            defaultLabel="All Roles"
            property={roleFilterProperty}
            selections={filter.Roles ?? []}
            onSelectionsChange={onRoleFilterChange}
            buttonTestID="customer-users-roles-filter"
            size={FormControlSizes.Small}
          />
        </Box>
        {isAdmin ? (
          <>
            <Divider orientation="vertical" m="spacing4" />
            <IconButton
              aria-label="Add user"
              icon={IconName.Plus}
              onClick={() => addEditCustomerUser()}
              variant={ButtonVariants.Positive}
              disabled={isAddUserDisabled}
              data-testid="add-user-button"
              size={FormControlSizes.Small}
            />
          </>
        ) : null}
      </HStack>
    </PanelHeader>
  );
}
